import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { KeyRound, Pencil, Plus, Trash2, UserX, UserCheck, Users, Settings2, Search, Upload, FileUser, Building2, Layers, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtFecha, ROL_LABEL, diasPorModoVacaciones } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useEmpresa } from "@/hooks/useEmpresa";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ImportarEmpleadosDialog from "@/components/ImportarEmpleadosDialog";
import { ImportarEmpleadosAyudaDialog, DescargarPlantillaEmpleadosCsv } from "@/components/ImportarEmpleadosAyuda";
import FotoTrabajadorEditor from "@/components/FotoTrabajadorEditor";
import PageHeader from "@/components/PageHeader";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { HorarioTramosEditor, tramosFromHorario, horarioParaGuardarTramos, resumenHorarioTramos, tramoDefecto, erroresSolapeTramos } from "@/components/HorarioTramosEditor";
import { HorarioModoConfig } from "@/components/HorarioModoConfig";
import { MODO_PATRON_TURNOS, mensajeObjetivoFlexible, resumenModoHorario } from "@/lib/horarioModos";
import { horarioDepartamento, horarioEditableDesdeOrigen } from "@/lib/horarioEditable";

const FORM_VACIO = {
  email: "", password: "", nombre: "", apellidos: "", dni_nie: "", telefono: "",
  direccion: "", poblacion: "", provincia: "",
  fecha_inicio_contrato: "", fecha_nacimiento: "", situacion: "activo", fecha_baja: "",
  tipo_contrato_id: "none",
  rol: "worker", departamento_id: "none", puesto_id: "none",
  dias_vacaciones: 30, vacaciones_modo: "empresa", usa_dias_empresa: true, pin: "", nfc_uid: "",
  dias_asuntos_propios: 0, usa_asuntos_empresa: true,
  vacaciones_usados: "0", asuntos_usados: "0",
  comm_email: true, comm_whatsapp: true, comm_sms: true, comm_telegram: false,
  cumpleanos_avisar_companeros: false,
};

function resumenContrato(t) {
  if (!t) return "—";
  return t.modo_jornada === "porcentaje" ? `${t.valor}%` : `${t.valor} h/semana`;
}

const PUESTO_SIN_COLOR = "#94a3b8";

function inicialesNombre(nombre, apellidos) {
  return `${nombre || ""} ${apellidos || ""}`.trim().split(/\s+/).filter(Boolean).map((p) => p[0]).slice(0, 2).join("").toUpperCase() || "?";
}

function esAdminGestion(u, gerenciaActiva) {
  return Boolean(gerenciaActiva && u?.rol === "admin");
}

function FilaEmpleado({ u, esAdmin, user, puestoColor, onEditar, onPass, onReset2fa, onToggle, gerenciaActiva }) {
  const color = puestoColor || PUESTO_SIN_COLOR;
  const soloPassword = esAdminGestion(u, gerenciaActiva);

  return (
    <tr className="border-b border-zinc-100 last:border-0 hover:bg-violet-50/30" style={{ borderLeftWidth: 3, borderLeftColor: soloPassword ? "#6366f1" : color }}>
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <span
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white shadow-sm"
            style={{ backgroundColor: soloPassword ? "#6366f1" : color }}
          >
            {inicialesNombre(u.nombre, u.apellidos)}
          </span>
          <div>
            {soloPassword ? (
              <span className="font-medium text-zinc-900">
                {u.nombre} {u.apellidos}
              </span>
            ) : (
              <Link
                to={`/admin/empleados/${u.id}`}
                className="group inline-flex items-center gap-1.5 font-medium text-indigo-700 hover:underline"
                data-testid={`ficha-empleado-${u.id}`}
              >
                {u.nombre} {u.apellidos}
                <FileUser className="h-3.5 w-3.5 opacity-0 transition-opacity group-hover:opacity-100" />
              </Link>
            )}
            <p className="text-xs text-zinc-400">{ROL_LABEL[u.rol]}</p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3 text-zinc-500">
        <p>{u.email}</p>
        {!soloPassword && u.telefono && <p className="text-xs">{u.telefono}</p>}
      </td>
      <td className="px-4 py-3 text-zinc-500">
        {soloPassword ? (
          <p className="text-xs text-zinc-400">Cuenta de gestión</p>
        ) : (
          <>
            <p>{u.tipo_contrato || "—"}</p>
            {u.tipo_contrato && (
              <p className="text-xs">
                {u.tipo_contrato_modo === "porcentaje" ? `${u.tipo_contrato_valor}%` : `${u.tipo_contrato_valor} h/sem`}
              </p>
            )}
            {u.fecha_inicio_contrato && (
              <p className="text-[10px] text-zinc-400">Desde {fmtFecha(u.fecha_inicio_contrato)}</p>
            )}
          </>
        )}
      </td>
      <td className="px-4 py-3">
        {soloPassword ? (
          <Badge variant="outline" className="border-indigo-200 bg-indigo-50 text-indigo-700">Administrador</Badge>
        ) : u.puesto ? (
          <Badge
            variant="outline"
            className="border-0 font-medium text-white"
            style={{ backgroundColor: color }}
          >
            {u.puesto}
          </Badge>
        ) : (
          <Badge variant="outline" className="border-zinc-200 bg-zinc-100 text-zinc-500">Sin puesto</Badge>
        )}
      </td>
      <td className="px-4 py-3">
        {soloPassword ? (
          <Badge variant="outline" className="border-indigo-200 bg-indigo-50 text-indigo-700">Gestión</Badge>
        ) : u.situacion === "baja" ? (
          <Badge variant="outline" className="border-red-200 bg-red-100 text-red-700">
            Baja {u.fecha_baja && `· ${fmtFecha(u.fecha_baja)}`}
          </Badge>
        ) : (
          <Badge variant="outline" className="border-green-200 bg-green-100 text-green-700">Activo</Badge>
        )}
        {u.totp_enabled && (
          <Badge variant="outline" className="mt-1 border-emerald-200 bg-emerald-50 text-emerald-700">
            2FA
          </Badge>
        )}
      </td>
      {esAdmin && (
        <td className="px-4 py-3">
          <div className="flex justify-end gap-1">
            {soloPassword ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => onPass(u)}
                data-testid={`reset-pass-${u.id}`}
                className="gap-1.5 border-indigo-200 text-indigo-700 hover:bg-indigo-50"
              >
                <KeyRound className="h-4 w-4" strokeWidth={1.5} />
                Cambiar contraseña
              </Button>
            ) : (
              <>
                <button onClick={() => onEditar(u)} data-testid={`editar-empleado-${u.id}`} className="rounded-md p-1.5 text-zinc-400 hover:bg-violet-100 hover:text-violet-700" title="Editar">
                  <Pencil className="h-4 w-4" strokeWidth={1.5} />
                </button>
                <button onClick={() => onPass(u)} data-testid={`reset-pass-${u.id}`} className="rounded-md p-1.5 text-zinc-400 hover:bg-violet-100 hover:text-violet-700" title="Restablecer contraseña">
                  <KeyRound className="h-4 w-4" strokeWidth={1.5} />
                </button>
                {(u.totp_enabled || u.totp_pending) && (
                  <button onClick={() => onReset2fa(u)} data-testid={`reset-2fa-${u.id}`} className="rounded-md p-1.5 text-zinc-400 hover:bg-amber-100 hover:text-amber-700" title="Resetear 2FA">
                    <ShieldOff className="h-4 w-4" strokeWidth={1.5} />
                  </button>
                )}
                {u.id !== user.id && (
                  <button onClick={() => onToggle(u)} data-testid={`toggle-activo-${u.id}`} className="rounded-md p-1.5 text-zinc-400 hover:bg-violet-100 hover:text-violet-700" title={u.activo ? "Dar de baja" : "Reactivar"}>
                    {u.activo ? <UserX className="h-4 w-4" strokeWidth={1.5} /> : <UserCheck className="h-4 w-4" strokeWidth={1.5} />}
                  </button>
                )}
              </>
            )}
          </div>
        </td>
      )}
    </tr>
  );
}

export default function Empleados() {
  const { user } = useAuth();
  const { empresa } = useEmpresa();
  const gerenciaActiva = !!empresa?.gestionar_gerencia;
  const esAdmin = user.rol === "admin";
  const [usuarios, setUsuarios] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [puestos, setPuestos] = useState([]);
  const [turnos, setTurnos] = useState([]);
  const [tiposContrato, setTiposContrato] = useState([]);
  const [open, setOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(FORM_VACIO);
  const [antiguedadOriginalEdit, setAntiguedadOriginalEdit] = useState("");
  const [confirmAntiguedadEmp, setConfirmAntiguedadEmp] = useState(false);
  const [passOpen, setPassOpen] = useState(null);
  const [nuevaPass, setNuevaPass] = useState("");
  const [reset2faOpen, setReset2faOpen] = useState(null);
  const [usarHorario, setUsarHorario] = useState(false);
  const [horarioTramos, setHorarioTramos] = useState([tramoDefecto()]);
  const [horarioModo, setHorarioModo] = useState(horarioEditableDesdeOrigen(null).modoConfig);
  const [tiposOpen, setTiposOpen] = useState(false);
  const [tipoForm, setTipoForm] = useState({ nombre: "", modo_jornada: "porcentaje", valor: "100" });
  const [editTipo, setEditTipo] = useState(null);
  const [centros, setCentros] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [busqueda, setBusqueda] = useState("");
  const [filtroCentro, setFiltroCentro] = useState("all");
  const [filtroDep, setFiltroDep] = useState("all");
  const [filtroPuesto, setFiltroPuesto] = useState("all");
  const [filtroActivo, setFiltroActivo] = useState("all");
  const [empresaCfg, setEmpresaCfg] = useState({
    vacaciones_modo: "naturales",
    dias_vacaciones: 30,
    dias_asuntos_propios: 0,
    asuntos_propios_activo: false,
  });
  const [importOpen, setImportOpen] = useState(false);
  const [telegramVinculado, setTelegramVinculado] = useState(false);
  const [enviandoVinculoTg, setEnviandoVinculoTg] = useState(false);

  const aplicarHorarioDesdeDepartamento = useCallback((depId, origen = null) => {
    const h = origen || horarioDepartamento(departamentos, depId);
    const editable = horarioEditableDesdeOrigen(h);
    setHorarioTramos(editable.tramos);
    setHorarioModo(editable.modoConfig);
  }, [departamentos]);

  const onToggleHorarioPersonalizado = (checked) => {
    setUsarHorario(checked);
    if (checked) {
      const origen = editando?.horario || horarioDepartamento(departamentos, form.departamento_id);
      aplicarHorarioDesdeDepartamento(form.departamento_id, origen);
    }
  };

  const modoEfectivoForm = (f = form) => {
    if (f.vacaciones_modo === "empresa") return empresaCfg.vacaciones_modo || "naturales";
    return f.vacaciones_modo;
  };

  const cambiarModoVacaciones = (modo) => {
    const modoEf = modo === "empresa" ? (empresaCfg.vacaciones_modo || "naturales") : modo;
    setForm((prev) => ({
      ...prev,
      vacaciones_modo: modo,
      dias_vacaciones: prev.usa_dias_empresa ? prev.dias_vacaciones : diasPorModoVacaciones(modoEf),
    }));
  };

  const cargar = useCallback(async () => {
    try {
      const [u, d, p, t, tc, r] = await Promise.all([
        api.get("/usuarios"),
        api.get("/departamentos"),
        api.get("/puestos"),
        esAdmin ? api.get("/turnos") : Promise.resolve({ data: [] }),
        esAdmin ? api.get("/tipos-contrato") : Promise.resolve({ data: [] }),
        api.get("/usuarios/resumen"),
      ]);
      setUsuarios(u.data);
      setDepartamentos(d.data);
      setPuestos(p.data);
      setTurnos(t.data);
      setTiposContrato(tc.data);
      setResumen(r.data);
      if (esAdmin) {
        const [c, emp] = await Promise.all([api.get("/centros"), api.get("/ajustes/empresa")]);
        setCentros(c.data);
        const e = emp.data.empresa;
        setEmpresaCfg({
          vacaciones_modo: e.vacaciones_modo || "naturales",
          dias_vacaciones: e.dias_vacaciones ?? e.efectivo?.dias_vacaciones ?? 30,
          dias_asuntos_propios: e.dias_asuntos_propios ?? e.efectivo?.dias_asuntos_propios ?? 0,
          asuntos_propios_activo: e.asuntos_propios_activo ?? e.efectivo?.asuntos_propios_activo ?? false,
          cumpleanos_activo: e.cumpleanos_activo ?? false,
        });
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [esAdmin]);

  useEffect(() => { cargar(); }, [cargar]);

  const depsFiltrados = useMemo(() => {
    if (filtroCentro === "all") return departamentos;
    return departamentos.filter((d) => d.centro_id === Number(filtroCentro));
  }, [departamentos, filtroCentro]);

  const puestosFiltrados = useMemo(() => {
    if (filtroDep !== "all") return puestos.filter((p) => p.departamento_id === Number(filtroDep));
    if (filtroCentro !== "all") {
      const depIds = new Set(depsFiltrados.map((d) => d.id));
      return puestos.filter((p) => depIds.has(p.departamento_id));
    }
    return puestos;
  }, [puestos, filtroDep, filtroCentro, depsFiltrados]);

  const usuariosFiltrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return usuarios.filter((u) => {
      if (filtroCentro !== "all" && u.centro_id !== Number(filtroCentro)) return false;
      if (filtroDep !== "all" && u.departamento_id !== Number(filtroDep)) return false;
      if (filtroPuesto !== "all" && u.puesto_id !== Number(filtroPuesto)) return false;
      if (filtroActivo === "activo" && u.situacion !== "activo") return false;
      if (filtroActivo === "baja" && u.situacion !== "baja") return false;
      if (!q) return true;
      const nombre = `${u.nombre} ${u.apellidos}`.toLowerCase();
      const tel = (u.telefono || "").replace(/\s/g, "");
      const qTel = q.replace(/\s/g, "");
      return nombre.includes(q) || u.email.toLowerCase().includes(q) || tel.includes(qTel);
    });
  }, [usuarios, busqueda, filtroCentro, filtroDep, filtroPuesto, filtroActivo]);

  const puestoColorById = useMemo(() => {
    const map = {};
    puestos.forEach((p) => { map[p.id] = p.color || PUESTO_SIN_COLOR; });
    return map;
  }, [puestos]);

  const centroColorById = useMemo(() => {
    const map = {};
    centros.forEach((c) => { map[c.id] = c.color || "#8b5cf6"; });
    return map;
  }, [centros]);

  const depColorById = useMemo(() => {
    const map = {};
    departamentos.forEach((d) => { map[d.id] = d.color || "#6366f1"; });
    return map;
  }, [departamentos]);

  const puestosEnVista = useMemo(() => {
    const ids = new Set(usuariosFiltrados.map((u) => u.puesto_id).filter(Boolean));
    return puestos.filter((p) => ids.has(p.id)).sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [usuariosFiltrados, puestos]);

  const usuariosAgrupados = useMemo(() => {
    const centrosMap = new Map();
    usuariosFiltrados.forEach((u) => {
      const cKey = u.centro_id ?? 0;
      if (!centrosMap.has(cKey)) {
        centrosMap.set(cKey, {
          centroId: u.centro_id,
          centroNombre: u.centro || "Sin centro",
          departamentos: new Map(),
        });
      }
      const centro = centrosMap.get(cKey);
      const dKey = u.departamento_id ?? 0;
      if (!centro.departamentos.has(dKey)) {
        centro.departamentos.set(dKey, {
          depId: u.departamento_id,
          depNombre: u.departamento || "Sin departamento",
          empleados: [],
        });
      }
      centro.departamentos.get(dKey).empleados.push(u);
    });
    return Array.from(centrosMap.values())
      .map((c) => ({
        ...c,
        departamentos: Array.from(c.departamentos.values())
          .map((d) => ({
            ...d,
            empleados: d.empleados.sort((a, b) => `${a.apellidos} ${a.nombre}`.localeCompare(`${b.apellidos} ${b.nombre}`)),
          }))
          .sort((a, b) => a.depNombre.localeCompare(b.depNombre)),
      }))
      .sort((a, b) => a.centroNombre.localeCompare(b.centroNombre));
  }, [usuariosFiltrados]);

  const subtituloLicencia = useMemo(() => {
    if (!resumen) return esAdmin ? "Gestiona el equipo, contratos y datos de contacto" : "Empleados de tu departamento";
    if (esAdmin && resumen.max_usuarios != null) {
      return `${resumen.total} empleados · ${resumen.disponibles} plazas disponibles (licencia ${resumen.total}/${resumen.max_usuarios})`;
    }
    return `${resumen.total} empleados en tu ámbito`;
  }, [resumen, esAdmin]);

  const abrirCrear = () => {
    setEditando(null);
    setAntiguedadOriginalEdit("");
    setForm({
      ...FORM_VACIO,
      dias_vacaciones: empresaCfg.dias_vacaciones ?? diasPorModoVacaciones(empresaCfg.vacaciones_modo),
      dias_asuntos_propios: empresaCfg.dias_asuntos_propios ?? 0,
    });
    setUsarHorario(false);
    setHorarioTramos([tramoDefecto()]);
    setHorarioModo(horarioEditableDesdeOrigen(null).modoConfig);
    setTelegramVinculado(false);
    setOpen(true);
  };

  const abrirEditar = async (u) => {
    if (esAdminGestion(u, gerenciaActiva)) {
      setPassOpen(u);
      return;
    }
    setEditando(u);
    setAntiguedadOriginalEdit(u.fecha_inicio_contrato || "");
    setForm({
      email: u.email,
      password: "",
      nombre: u.nombre,
      apellidos: u.apellidos,
      dni_nie: u.dni_nie || "",
      telefono: u.telefono || "",
      direccion: u.direccion || "",
      poblacion: u.poblacion || "",
      provincia: u.provincia || "",
      fecha_inicio_contrato: u.fecha_inicio_contrato || "",
      fecha_nacimiento: u.fecha_nacimiento || "",
      situacion: u.situacion || "activo",
      fecha_baja: u.fecha_baja || "",
      tipo_contrato_id: u.tipo_contrato_id ? String(u.tipo_contrato_id) : "none",
      rol: u.rol,
      departamento_id: u.departamento_id ? String(u.departamento_id) : "none",
      puesto_id: u.puesto_id ? String(u.puesto_id) : "none",
      dias_vacaciones: u.dias_vacaciones ?? u.dias_vacaciones_efectivos ?? empresaCfg.dias_vacaciones,
      vacaciones_modo: u.vacaciones_modo || "empresa",
      usa_dias_empresa: u.usa_dias_empresa ?? u.dias_vacaciones == null,
      dias_asuntos_propios: u.dias_asuntos_propios ?? u.dias_asuntos_propios_efectivos ?? empresaCfg.dias_asuntos_propios,
      usa_asuntos_empresa: u.usa_asuntos_empresa ?? u.dias_asuntos_propios == null,
      vacaciones_usados: "0",
      asuntos_usados: "0",
      pin: "",
      nfc_uid: u.nfc_uid || "",
      comm_email: u.comm_email !== false,
      comm_whatsapp: u.comm_whatsapp !== false,
      comm_sms: u.comm_sms !== false,
      comm_telegram: !!u.comm_telegram,
      cumpleanos_avisar_companeros: !!u.cumpleanos_avisar_companeros,
    });
    setTelegramVinculado(!!u.telegram_vinculado);
    setUsarHorario(!!u.horario);
    if (u.horario) {
      const editable = horarioEditableDesdeOrigen(u.horario);
      setHorarioTramos(editable.tramos);
      setHorarioModo(editable.modoConfig);
    } else {
      const editable = horarioEditableDesdeOrigen(horarioDepartamento(departamentos, u.departamento_id));
      setHorarioTramos(editable.tramos);
      setHorarioModo(editable.modoConfig);
    }
    setOpen(true);
    try {
      const { data: ficha } = await api.get(`/usuarios/${u.id}/ficha`);
      setForm((prev) => ({
        ...prev,
        vacaciones_usados: String(ficha?.vacaciones?.usados_ajuste ?? 0),
        asuntos_usados: String(ficha?.asuntos_propios?.usados_ajuste ?? 0),
      }));
    } catch {
      /* si falla la ficha, dejamos 0 */
    }
  };

  const guardar = async (confirmarAntiguedad = false) => {
    if (usarHorario && horarioModo.modo !== MODO_PATRON_TURNOS) {
      const errs = erroresSolapeTramos(horarioTramos);
      if (errs.length) {
        toast.error(errs[0]);
        return;
      }
    }
    if (usarHorario) {
      const avisoObjetivo = mensajeObjetivoFlexible(horarioModo);
      if (avisoObjetivo && !window.confirm(avisoObjetivo)) return;
    }
    const cambioAntiguedad =
      editando && (form.fecha_inicio_contrato || "") !== (antiguedadOriginalEdit || "");
    if (cambioAntiguedad && !confirmarAntiguedad) {
      setConfirmAntiguedadEmp(true);
      return;
    }
    const payload = {
      email: form.email,
      nombre: form.nombre,
      apellidos: form.apellidos,
      dni_nie: form.dni_nie,
      telefono: form.telefono,
      direccion: form.direccion,
      poblacion: form.poblacion,
      provincia: form.provincia,
      fecha_inicio_contrato: form.fecha_inicio_contrato || null,
      fecha_nacimiento: form.fecha_nacimiento || null,
      situacion: form.situacion,
      fecha_baja: form.situacion === "baja" ? (form.fecha_baja || null) : null,
      tipo_contrato_id: form.tipo_contrato_id === "none" ? null : Number(form.tipo_contrato_id),
      rol: form.rol,
      departamento_id: form.departamento_id === "none" ? null : Number(form.departamento_id),
      puesto_id: form.puesto_id === "none" ? null : Number(form.puesto_id),
      dias_vacaciones: form.usa_dias_empresa ? null : Number(form.dias_vacaciones),
      vacaciones_modo: form.vacaciones_modo === "empresa" ? null : form.vacaciones_modo,
      dias_asuntos_propios: form.usa_asuntos_empresa ? null : Number(form.dias_asuntos_propios),
      comm_email: form.comm_email,
      comm_whatsapp: form.comm_whatsapp,
      comm_sms: form.comm_sms,
      comm_telegram: form.comm_telegram,
      cumpleanos_avisar_companeros: form.cumpleanos_avisar_companeros,
      horario: usarHorario ? horarioParaGuardarTramos(horarioTramos, horarioModo) : null,
    };
    if (cambioAntiguedad) payload.confirmar_cambio_antiguedad = true;
    if (form.pin.trim() !== "") payload.pin = form.pin.trim();
    if (editando && form.nfc_uid.trim() !== (editando.nfc_uid || "").trim()) {
      payload.nfc_uid = form.nfc_uid.trim().toUpperCase().replace(/[^0-9A-Fa-f]/g, "");
    } else if (!editando && form.nfc_uid.trim() !== "") {
      payload.nfc_uid = form.nfc_uid.trim().toUpperCase().replace(/[^0-9A-Fa-f]/g, "");
    }
    const vacUsados = Number(form.vacaciones_usados || 0);
    const aspUsados = Number(form.asuntos_usados || 0);
    try {
      if (editando) {
        await api.put(`/usuarios/${editando.id}`, payload);
        await api.put(`/usuarios/${editando.id}/dias-usados`, {
          anio: new Date().getFullYear(),
          vacaciones_usados: vacUsados,
          asuntos_usados: aspUsados,
          motivo: "Consumo previo a la incorporación / corrección en ficha",
        });
        toast.success("Empleado actualizado");
      } else {
        if (!form.password || form.password.length < 6) {
          toast.error("La contraseña debe tener al menos 6 caracteres");
          return;
        }
        await api.post("/usuarios", {
          ...payload,
          password: form.password,
          vacaciones_usados: vacUsados,
          asuntos_usados: aspUsados,
        });
        toast.success("Empleado creado");
      }
      setConfirmAntiguedadEmp(false);
      setOpen(false);
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleActivo = async (u) => {
    try {
      if (u.activo) {
        await api.delete(`/usuarios/${u.id}`);
        toast.success("Empleado dado de baja");
      } else {
        await api.put(`/usuarios/${u.id}`, { activo: true, situacion: "activo", fecha_baja: null });
        toast.success("Empleado reactivado");
      }
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const resetPass = async () => {
    try {
      await api.put(`/usuarios/${passOpen.id}/password`, { password: nuevaPass });
      toast.success("Contraseña actualizada");
      setPassOpen(null);
      setNuevaPass("");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const reset2fa = async () => {
    try {
      await api.post(`/usuarios/${reset2faOpen.id}/2fa/reset`);
      toast.success("2FA desactivado para el empleado");
      setReset2faOpen(null);
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const guardarTipo = async () => {
    try {
      const payload = {
        nombre: tipoForm.nombre,
        modo_jornada: tipoForm.modo_jornada,
        valor: Number(tipoForm.valor),
      };
      if (editTipo) {
        await api.put(`/tipos-contrato/${editTipo.id}`, payload);
        toast.success("Tipo de contrato actualizado");
      } else {
        await api.post("/tipos-contrato", payload);
        toast.success("Tipo de contrato creado");
      }
      setEditTipo(null);
      setTipoForm({ nombre: "", modo_jornada: "porcentaje", valor: "100" });
      const { data } = await api.get("/tipos-contrato");
      setTiposContrato(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const eliminarTipo = async (t) => {
    if (!window.confirm(`¿Eliminar el tipo "${t.nombre}"?`)) return;
    try {
      await api.delete(`/tipos-contrato/${t.id}`);
      toast.success("Tipo eliminado");
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="empleados-page">
      <PageHeader
        icon={Users}
        title={`Empleados${resumen ? ` (${usuariosFiltrados.length}${usuariosFiltrados.length !== usuarios.length ? ` de ${usuarios.length}` : ""})` : ""}`}
        subtitle={subtituloLicencia}
        sectionKey="empleados"
        brandGradient={false}
      >
        {esAdmin && (
          <>
            <ImportarEmpleadosAyudaDialog />
            <DescargarPlantillaEmpleadosCsv
              className="border-white/40 bg-white/10 text-white hover:bg-white/20 hover:text-white"
              variant="outline"
              size="sm"
            />
            <Button onClick={() => setImportOpen(true)} className="gap-1.5 bg-white/20 text-white hover:bg-white/30">
              <Upload className="h-4 w-4" /> Importar CSV
            </Button>
            <Button onClick={abrirCrear} data-testid="crear-empleado-btn" className="gap-1.5 bg-white/20 text-white hover:bg-white/30">
              <Plus className="h-4 w-4" />
              Nuevo empleado
            </Button>
          </>
        )}
      </PageHeader>

      <div className="rounded-2xl border border-violet-100 bg-gradient-to-r from-violet-50/80 to-fuchsia-50/80 p-4 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
          <div className="relative min-w-[200px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-violet-400" />
            <Input
              className="border-violet-100 bg-white pl-9"
              placeholder="Buscar por nombre, email o teléfono…"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              data-testid="empleados-busqueda"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {esAdmin && (
              <select
                className="h-9 min-w-[130px] rounded-md border border-violet-100 bg-white px-2 text-sm"
                value={filtroCentro}
                onChange={(e) => { setFiltroCentro(e.target.value); setFiltroDep("all"); setFiltroPuesto("all"); }}
                data-testid="filtro-centro"
              >
                <option value="all">Todos los centros</option>
                {centros.map((c) => (
                  <option key={c.id} value={c.id}>{c.nombre}</option>
                ))}
              </select>
            )}
            <select
              className="h-9 min-w-[140px] rounded-md border border-violet-100 bg-white px-2 text-sm"
              value={filtroDep}
              onChange={(e) => { setFiltroDep(e.target.value); setFiltroPuesto("all"); }}
              data-testid="filtro-departamento"
            >
              <option value="all">Todos los deptos</option>
              {depsFiltrados.map((d) => (
                <option key={d.id} value={d.id}>{d.nombre}</option>
              ))}
            </select>
            <select
              className="h-9 min-w-[130px] rounded-md border border-violet-100 bg-white px-2 text-sm"
              value={filtroPuesto}
              onChange={(e) => setFiltroPuesto(e.target.value)}
              data-testid="filtro-puesto"
            >
              <option value="all">Todos los puestos</option>
              {puestosFiltrados.map((p) => (
                <option key={p.id} value={p.id}>{p.nombre}</option>
              ))}
            </select>
            <select
              className="h-9 min-w-[110px] rounded-md border border-violet-100 bg-white px-2 text-sm"
              value={filtroActivo}
              onChange={(e) => setFiltroActivo(e.target.value)}
              data-testid="filtro-activo"
            >
              <option value="all">Todos</option>
              <option value="activo">Activos</option>
              <option value="baja">De baja</option>
            </select>
          </div>
        </div>
        {esAdmin && resumen?.max_usuarios != null && (
          <p className="mt-2 text-xs text-violet-600/80">
            Licencia: {resumen.total} de {resumen.max_usuarios} plazas usadas
            {resumen.disponibles > 0 ? ` · ${resumen.disponibles} disponibles` : " · límite alcanzado"}
          </p>
        )}
      </div>

      {puestosEnVista.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-zinc-200 bg-white px-4 py-2.5 text-xs shadow-sm">
          <span className="font-semibold uppercase tracking-wide text-zinc-400">Puestos</span>
          {puestosEnVista.map((p) => (
            <span key={p.id} className="inline-flex items-center gap-1.5 rounded-full border border-zinc-100 bg-zinc-50 px-2.5 py-1 text-zinc-700">
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: p.color || PUESTO_SIN_COLOR }} />
              {p.nombre}
            </span>
          ))}
        </div>
      )}

      <div className="space-y-4" data-testid="empleados-tabla">
        {usuariosAgrupados.map((centro) => {
          const centroColor = centro.centroId ? (centroColorById[centro.centroId] || "#8b5cf6") : "#94a3b8";
          const totalCentro = centro.departamentos.reduce((n, d) => n + d.empleados.length, 0);
          return (
            <div key={centro.centroId ?? "sin-centro"} className="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm">
              <div
                className="flex items-center justify-between gap-3 border-b border-zinc-100 px-4 py-3"
                style={{ background: `linear-gradient(to right, ${centroColor}12, transparent)` }}
              >
                <div className="flex items-center gap-2.5">
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg text-white shadow-sm" style={{ backgroundColor: centroColor }}>
                    <Building2 className="h-4 w-4" />
                  </span>
                  <div>
                    <p className="font-semibold text-zinc-900">{centro.centroNombre}</p>
                    <p className="text-xs text-zinc-500">{totalCentro} empleado{totalCentro !== 1 ? "s" : ""}</p>
                  </div>
                </div>
              </div>

              {centro.departamentos.map((dep) => {
                const depColor = dep.depId ? (depColorById[dep.depId] || "#6366f1") : "#cbd5e1";
                return (
                  <div key={dep.depId ?? "sin-dep"} className="border-b border-zinc-100 last:border-b-0">
                    <div
                      className="flex items-center gap-2 border-b border-zinc-50 px-4 py-2"
                      style={{ borderLeftWidth: 4, borderLeftColor: depColor, backgroundColor: `${depColor}08` }}
                    >
                      <Layers className="h-3.5 w-3.5" style={{ color: depColor }} />
                      <span className="text-sm font-medium text-zinc-800">{dep.depNombre}</span>
                      <span className="text-xs text-zinc-400">({dep.empleados.length})</span>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-violet-50 bg-violet-50/40 text-left text-[11px] font-semibold uppercase tracking-[0.1em] text-violet-700">
                            <th className="px-4 py-2.5">Nombre</th>
                            <th className="px-4 py-2.5">Contacto</th>
                            <th className="px-4 py-2.5">Contrato</th>
                            <th className="px-4 py-2.5">Puesto</th>
                            <th className="px-4 py-2.5">Situación</th>
                            {esAdmin && <th className="px-4 py-2.5 text-right">Acciones</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {dep.empleados.map((u) => (
                            <FilaEmpleado
                              key={u.id}
                              u={u}
                              esAdmin={esAdmin}
                              user={user}
                              puestoColor={u.puesto_id ? puestoColorById[u.puesto_id] : PUESTO_SIN_COLOR}
                              onEditar={abrirEditar}
                              onPass={setPassOpen}
                              onReset2fa={setReset2faOpen}
                              onToggle={toggleActivo}
                              gerenciaActiva={gerenciaActiva}
                            />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}

        {usuariosFiltrados.length === 0 && (
          <div className="rounded-2xl border border-zinc-200 bg-white p-8 text-center text-zinc-400 shadow-sm">
            {usuarios.length === 0 ? "No hay empleados." : "Ningún empleado coincide con los filtros."}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] w-[95vw] max-w-4xl overflow-y-auto" data-testid="empleado-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editando ? "Editar empleado" : "Nuevo empleado"}</DialogTitle>
            <DialogDescription>Datos personales, contrato, organización y horario.</DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="personal">
            <TabsList className="mb-4 w-full justify-start">
              <TabsTrigger value="personal">Datos personales</TabsTrigger>
              <TabsTrigger value="contrato">Contrato</TabsTrigger>
              <TabsTrigger value="organizacion">Organización</TabsTrigger>
            </TabsList>

            <TabsContent value="personal" className="space-y-4">
              {editando && (
                <div className="rounded-lg border border-indigo-100 bg-indigo-50/40 p-4">
                  <p className="mb-3 text-sm font-medium text-indigo-950">Foto del trabajador</p>
                  <FotoTrabajadorEditor
                    fotoUrlProp={editando.foto_url}
                    uploadUrl={`/usuarios/${editando.id}/foto`}
                    deleteUrl={`/usuarios/${editando.id}/foto`}
                    onUpdated={(url) => {
                      setEditando((prev) => (prev ? { ...prev, foto_url: url } : prev));
                      setUsuarios((list) => list.map((x) => (x.id === editando.id ? { ...x, foto_url: url } : x)));
                    }}
                  />
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Nombre *</Label>
                  <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="empleado-nombre-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>Apellidos *</Label>
                  <Input value={form.apellidos} onChange={(e) => setForm({ ...form, apellidos: e.target.value })} data-testid="empleado-apellidos-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>DNI/NIE</Label>
                  <Input
                    value={form.dni_nie}
                    onChange={(e) => setForm({ ...form, dni_nie: e.target.value.toUpperCase() })}
                    placeholder="12345678A"
                    maxLength={20}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Fecha de nacimiento</Label>
                  <Input
                    type="date"
                    value={form.fecha_nacimiento}
                    onChange={(e) => setForm({ ...form, fecha_nacimiento: e.target.value })}
                  />
                </div>
              </div>
              {empresaCfg.cumpleanos_activo && (
                <div className="rounded-lg border border-pink-100 bg-pink-50/40 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-pink-950">Informar a compañeros del centro</p>
                      <p className="text-xs text-pink-800/70">
                        Si está activo, el resto de trabajadores del mismo centro recibirán un aviso el día de su cumpleaños
                      </p>
                    </div>
                    <Switch
                      checked={form.cumpleanos_avisar_companeros}
                      disabled={!form.fecha_nacimiento}
                      onCheckedChange={(v) => setForm({ ...form, cumpleanos_avisar_companeros: v })}
                    />
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Teléfono de contacto</Label>
                  <Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} placeholder="600 000 000" />
                </div>
                <div className="space-y-1.5">
                  <Label>Email *</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="empleado-email-input" />
                </div>
              </div>
              <div className="rounded-lg border border-indigo-100 bg-indigo-50/40 p-4 space-y-3">
                <p className="text-sm font-medium text-indigo-950">Envío de comunicaciones y notificaciones</p>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm">Email</p>
                    <p className="text-xs text-zinc-500">Solicitudes, revisiones y avisos por correo</p>
                  </div>
                  <Switch checked={form.comm_email} onCheckedChange={(v) => setForm({ ...form, comm_email: v })} data-testid="empleado-comm-email" />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm">WhatsApp</p>
                    <p className="text-xs text-zinc-500">Mismos avisos al móvil (requiere teléfono)</p>
                  </div>
                  <Switch checked={form.comm_whatsapp} onCheckedChange={(v) => setForm({ ...form, comm_whatsapp: v })} data-testid="empleado-comm-whatsapp" />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm">SMS</p>
                    <p className="text-xs text-zinc-500">Avisos por SMS vía Altiria (requiere teléfono)</p>
                  </div>
                  <Switch checked={form.comm_sms} onCheckedChange={(v) => setForm({ ...form, comm_sms: v })} data-testid="empleado-comm-sms" />
                </div>
                <div className="space-y-2 rounded-md border border-sky-100 bg-white/70 p-3">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm">Telegram</p>
                      <p className="text-xs text-zinc-500">
                        {telegramVinculado
                          ? "Cuenta vinculada (chat_id guardado)"
                          : "Requiere vínculo por email tras activar Telegram en Ajustes"}
                      </p>
                    </div>
                    <Switch
                      checked={form.comm_telegram}
                      onCheckedChange={(v) => setForm({ ...form, comm_telegram: v })}
                      data-testid="empleado-comm-telegram"
                    />
                  </div>
                  {editando && (
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="gap-1.5 border-sky-200 text-sky-800"
                        disabled={enviandoVinculoTg}
                        data-testid="empleado-telegram-vincular"
                        onClick={async () => {
                          setEnviandoVinculoTg(true);
                          try {
                            await api.put(`/usuarios/${editando.id}`, { comm_telegram: form.comm_telegram });
                            const { data } = await api.post(`/usuarios/${editando.id}/telegram/vincular`);
                            toast.success(data.mensaje);
                            setTelegramVinculado(!!data.telegram_vinculado);
                            setForm((f) => ({ ...f, comm_telegram: true }));
                          } catch (e) {
                            toast.error(formatApiError(e));
                          } finally {
                            setEnviandoVinculoTg(false);
                          }
                        }}
                      >
                        {enviandoVinculoTg ? "Enviando…" : "Enviar vínculo"}
                      </Button>
                      {telegramVinculado && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-zinc-500"
                          onClick={async () => {
                            try {
                              const { data } = await api.post(`/usuarios/${editando.id}/telegram/desvincular`);
                              toast.success(data.mensaje);
                              setTelegramVinculado(false);
                            } catch (e) {
                              toast.error(formatApiError(e));
                            }
                          }}
                        >
                          Desvincular
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {!editando && (
                <div className="space-y-1.5">
                  <Label>Contraseña *</Label>
                  <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Mínimo 6 caracteres" data-testid="empleado-password-input" />
                </div>
              )}
              <div className="space-y-1.5">
                <Label>Dirección</Label>
                <Input value={form.direccion} onChange={(e) => setForm({ ...form, direccion: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Población</Label>
                  <Input value={form.poblacion} onChange={(e) => setForm({ ...form, poblacion: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Provincia</Label>
                  <Input value={form.provincia} onChange={(e) => setForm({ ...form, provincia: e.target.value })} />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="contrato" className="space-y-4">
              {editando && (
                <p className="rounded-lg border border-amber-100 bg-amber-50/80 px-3 py-2 text-xs text-amber-950">
                  Para un cambio de jornada o tipo de contrato con historial (cerrar el anterior y abrir uno nuevo),
                  usa la{" "}
                  <Link to={`/admin/empleados/${editando.id}`} className="font-semibold underline">
                    ficha del empleado → Cambio de contrato
                  </Link>
                  . Aquí solo correcciones puntuales.
                </p>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Inicio contrato (antigüedad)</Label>
                  <Input type="date" value={form.fecha_inicio_contrato} onChange={(e) => setForm({ ...form, fecha_inicio_contrato: e.target.value })} />
                  <p className="text-[11px] text-zinc-500">Afecta al prorrateo de vacaciones. No la uses como fecha del cambio de jornada.</p>
                </div>
                <div className="space-y-1.5">
                  <Label>Situación</Label>
                  <Select value={form.situacion} onValueChange={(v) => setForm({ ...form, situacion: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="activo">Activo</SelectItem>
                      <SelectItem value="baja">Baja</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {form.situacion === "baja" && (
                <div className="space-y-1.5">
                  <Label>Fecha de baja</Label>
                  <Input type="date" value={form.fecha_baja} onChange={(e) => setForm({ ...form, fecha_baja: e.target.value })} />
                </div>
              )}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label>Tipo de contrato</Label>
                  {esAdmin && (
                    <Button type="button" size="sm" variant="ghost" className="h-7 gap-1 text-xs" onClick={() => setTiposOpen(true)}>
                      <Settings2 className="h-3 w-3" /> Gestionar tipos
                    </Button>
                  )}
                </div>
                <Select value={form.tipo_contrato_id} onValueChange={(v) => setForm({ ...form, tipo_contrato_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar tipo" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin especificar</SelectItem>
                    {tiposContrato.map((t) => (
                      <SelectItem key={t.id} value={String(t.id)}>
                        {t.nombre} · {resumenContrato(t)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-3 rounded-lg border border-violet-100 bg-violet-50/40 p-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium">Días de vacaciones</p>
                    <p className="text-xs text-zinc-500">
                      Empresa: {empresaCfg.dias_vacaciones} d ({empresaCfg.vacaciones_modo === "habiles" ? "hábiles" : "naturales"})
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label htmlFor="usa-dias-empresa" className="text-xs text-zinc-600">Heredar empresa</Label>
                    <Switch
                      id="usa-dias-empresa"
                      checked={form.usa_dias_empresa}
                      onCheckedChange={(v) => setForm({
                        ...form,
                        usa_dias_empresa: v,
                        dias_vacaciones: v ? (empresaCfg.dias_vacaciones ?? 30) : diasPorModoVacaciones(modoEfectivoForm()),
                      })}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Días vacaciones / año</Label>
                    <Input
                      type="number"
                      min="0"
                      disabled={form.usa_dias_empresa}
                      value={form.usa_dias_empresa ? (empresaCfg.dias_vacaciones ?? 30) : form.dias_vacaciones}
                      onChange={(e) => setForm({ ...form, dias_vacaciones: e.target.value })}
                      data-testid="empleado-vacaciones-input"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Conteo vacaciones</Label>
                    <Select value={form.vacaciones_modo} onValueChange={cambiarModoVacaciones}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="empresa">Heredar de la empresa</SelectItem>
                        <SelectItem value="naturales">Días naturales (30 por defecto)</SelectItem>
                        <SelectItem value="habiles">Días hábiles (22 por defecto)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              {empresaCfg.asuntos_propios_activo && (
              <div className="space-y-3 rounded-lg border border-amber-100 bg-amber-50/40 p-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium">Días de asuntos propios</p>
                    <p className="text-xs text-zinc-500">
                      Empresa: {empresaCfg.dias_asuntos_propios ?? 0} d al año
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label htmlFor="usa-asuntos-empresa" className="text-xs text-zinc-600">Heredar empresa</Label>
                    <Switch
                      id="usa-asuntos-empresa"
                      checked={form.usa_asuntos_empresa}
                      onCheckedChange={(v) => setForm({
                        ...form,
                        usa_asuntos_empresa: v,
                        dias_asuntos_propios: v ? (empresaCfg.dias_asuntos_propios ?? 0) : form.dias_asuntos_propios,
                      })}
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Días asuntos propios / año</Label>
                  <Input
                    type="number"
                    min="0"
                    max="366"
                    disabled={form.usa_asuntos_empresa}
                    value={form.usa_asuntos_empresa ? (empresaCfg.dias_asuntos_propios ?? 0) : form.dias_asuntos_propios}
                    onChange={(e) => setForm({ ...form, dias_asuntos_propios: e.target.value })}
                    data-testid="empleado-asuntos-propios-input"
                  />
                </div>
              </div>
              )}
              <div className="space-y-3 rounded-lg border border-sky-100 bg-sky-50/40 p-3">
                <div>
                  <p className="text-sm font-medium">Días ya consumidos este año</p>
                  <p className="text-xs text-zinc-500">
                    Si el trabajador ya disfrutó vacaciones o asuntos fuera del sistema (alta a mitad de año),
                    indícalos aquí. Restan el cupo de {new Date().getFullYear()}; en enero no se reaplica
                    (el remanente pasa por arrastre).
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Vacaciones usadas</Label>
                    <Input
                      type="number"
                      min="0"
                      max="366"
                      value={form.vacaciones_usados}
                      onChange={(e) => setForm({ ...form, vacaciones_usados: e.target.value })}
                      data-testid="empleado-vacaciones-usados-input"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Asuntos propios usados</Label>
                    <Input
                      type="number"
                      min="0"
                      max="366"
                      value={form.asuntos_usados}
                      onChange={(e) => setForm({ ...form, asuntos_usados: e.target.value })}
                      data-testid="empleado-asuntos-usados-input"
                      disabled={!empresaCfg.asuntos_propios_activo}
                    />
                  </div>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>PIN quiosco (4 dígitos)</Label>
                <Input inputMode="numeric" maxLength={4} placeholder={editando?.tiene_pin ? "•••• (asignado)" : "Opcional"} value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "") })} data-testid="empleado-pin-input" />
              </div>
              <div className="space-y-1.5">
                <Label>UID tarjeta NFC</Label>
                <Input
                  placeholder={editando?.tiene_nfc ? "UID asignado (edita para cambiar)" : "Ej. C3EE8337 — leer con NFC.Reader"}
                  value={form.nfc_uid}
                  onChange={(e) => setForm({ ...form, nfc_uid: e.target.value.toUpperCase().replace(/[^0-9A-F]/g, "") })}
                  data-testid="empleado-nfc-uid-input"
                />
                <p className="text-xs text-muted-foreground">Pega el UID de la tarjeta (sin programar la tarjeta). Vacío quita la asignación.</p>
              </div>
            </TabsContent>

            <TabsContent value="organizacion" className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Rol</Label>
                  <Select value={form.rol} onValueChange={(v) => setForm({ ...form, rol: v })}>
                    <SelectTrigger data-testid="empleado-rol-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="worker">Trabajador</SelectItem>
                      <SelectItem value="manager">Encargado</SelectItem>
                      <SelectItem value="admin">Administrador</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Departamento</Label>
                  <Select value={form.departamento_id} onValueChange={(v) => setForm({ ...form, departamento_id: v, puesto_id: "none" })}>
                    <SelectTrigger data-testid="empleado-departamento-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin departamento</SelectItem>
                      {departamentos.map((d) => (
                        <SelectItem key={d.id} value={String(d.id)}>{d.nombre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Puesto de trabajo</Label>
                <Select value={form.puesto_id} onValueChange={(v) => setForm({ ...form, puesto_id: v })}>
                  <SelectTrigger data-testid="empleado-puesto-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin puesto</SelectItem>
                    {puestos.filter((p) => form.departamento_id === "none" || p.departamento_id === Number(form.departamento_id)).map((p) => (
                      <SelectItem key={p.id} value={String(p.id)}>{p.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between rounded-lg border border-violet-100 bg-violet-50/50 px-3 py-2.5">
                  <div>
                    <p className="text-sm font-medium">Horario personalizado</p>
                    <p className="text-xs text-zinc-400">Si está desactivado, hereda el horario del departamento (o del centro si el departamento no tiene horario propio). Si lo personalizas, el cambio aplica como mínimo el día siguiente al fin de la jornada vigente.</p>
                  </div>
                  <Switch checked={usarHorario} onCheckedChange={onToggleHorarioPersonalizado} data-testid="empleado-horario-switch" />
                </div>
                {!usarHorario && form.departamento_id !== "none" && (
                  <div className="rounded-lg border border-zinc-100 bg-zinc-50 px-3 py-2 text-xs text-zinc-600">
                    <p className="font-medium text-zinc-800">
                      {departamentos.find((d) => d.id === Number(form.departamento_id))?.hereda_horario_centro
                        ? "Horario heredado del centro"
                        : "Horario heredado del departamento"}
                    </p>
                    <p className="mt-0.5 text-indigo-700">{resumenModoHorario(horarioDepartamento(departamentos, form.departamento_id))}</p>
                    <p>{resumenHorarioTramos(horarioDepartamento(departamentos, form.departamento_id))}</p>
                  </div>
                )}
                {usarHorario && (
                  <>
                    <p className="text-xs text-zinc-500">
                      Se ha cargado la configuración del departamento/centro como punto de partida. Puedes ajustarla para este trabajador.
                    </p>
                    <HorarioModoConfig config={horarioModo} onChange={setHorarioModo} plantillas={turnos} />
                    {horarioModo.modo !== MODO_PATRON_TURNOS && (
                      <HorarioTramosEditor
                        tramos={horarioTramos}
                        onChange={setHorarioTramos}
                        prefix="empleado-tramos"
                        plantillas={turnos}
                        modo={horarioModo.modo}
                      />
                    )}
                  </>
                )}
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button onClick={() => guardar(false)} disabled={!form.nombre.trim() || !form.email.trim()} data-testid="empleado-guardar-btn" className="bg-violet-600 hover:bg-violet-700">
              {editando ? "Guardar cambios" : "Crear empleado"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={tiposOpen} onOpenChange={setTiposOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Tipos de contrato</DialogTitle>
            <DialogDescription>Completo, parcial, prácticas u otros. Define horas semanales o porcentaje de jornada.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {tiposContrato.map((t) => (
              <div key={t.id} className="flex items-center justify-between rounded-lg border px-3 py-2">
                <div>
                  <p className="text-sm font-medium">{t.nombre}</p>
                  <p className="text-xs text-zinc-400">{resumenContrato(t)} · {t.num_empleados} empleados</p>
                </div>
                <div className="flex gap-1">
                  <button type="button" className="rounded p-1 text-zinc-400 hover:text-violet-600" onClick={() => { setEditTipo(t); setTipoForm({ nombre: t.nombre, modo_jornada: t.modo_jornada, valor: String(t.valor) }); }}>
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button type="button" className="rounded p-1 text-zinc-400 hover:text-red-500" onClick={() => eliminarTipo(t)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
            <div className="rounded-lg border border-dashed p-3 space-y-2">
              <p className="text-xs font-semibold uppercase text-zinc-400">{editTipo ? "Editar tipo" : "Nuevo tipo"}</p>
              <Input placeholder="Nombre (ej. Media jornada)" value={tipoForm.nombre} onChange={(e) => setTipoForm({ ...tipoForm, nombre: e.target.value })} />
              <div className="grid grid-cols-2 gap-2">
                <select className="h-9 rounded-md border px-2 text-sm" value={tipoForm.modo_jornada} onChange={(e) => setTipoForm({ ...tipoForm, modo_jornada: e.target.value })}>
                  <option value="porcentaje">Porcentaje (%)</option>
                  <option value="horas">Horas / semana</option>
                </select>
                <Input type="number" min="0" step="0.5" value={tipoForm.valor} onChange={(e) => setTipoForm({ ...tipoForm, valor: e.target.value })} />
              </div>
              <Button size="sm" onClick={guardarTipo} disabled={!tipoForm.nombre.trim()} className="w-full bg-violet-600 hover:bg-violet-700">
                {editTipo ? "Guardar cambios" : "Añadir tipo"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!passOpen} onOpenChange={(v) => !v && setPassOpen(null)}>
        <DialogContent data-testid="reset-pass-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Cambiar contraseña</DialogTitle>
            <DialogDescription>
              {passOpen?.rol === "admin" && gerenciaActiva
                ? "Cuenta de gestión de la empresa. Solo se puede cambiar la contraseña de acceso."
                : "Establece una nueva contraseña para este empleado."}
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm text-zinc-500">
            Nueva contraseña para <span className="font-medium text-zinc-950">{passOpen?.nombre} {passOpen?.apellidos}</span>
            {passOpen?.email && (
              <span className="block text-xs text-zinc-400">{passOpen.email}</span>
            )}
          </p>
          <Input type="password" placeholder="Mínimo 6 caracteres" value={nuevaPass} onChange={(e) => setNuevaPass(e.target.value)} data-testid="reset-pass-input" />
          <DialogFooter>
            <Button onClick={resetPass} disabled={nuevaPass.length < 6} data-testid="reset-pass-submit" className="bg-violet-600 hover:bg-violet-700">
              Cambiar contraseña
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!reset2faOpen} onOpenChange={(v) => !v && setReset2faOpen(null)}>
        <AlertDialogContent data-testid="reset-2fa-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Resetear verificación en dos pasos?</AlertDialogTitle>
            <AlertDialogDescription>
              Se desactivará el 2FA de {reset2faOpen?.nombre} {reset2faOpen?.apellidos}. Podrá volver a
              activarlo desde su perfil. Úsalo si ha perdido el móvil o los códigos de recuperación.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={reset2fa} data-testid="reset-2fa-confirm">
              Resetear 2FA
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmAntiguedadEmp} onOpenChange={setConfirmAntiguedadEmp}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Cambiar la antigüedad?</AlertDialogTitle>
            <AlertDialogDescription>
              Vas a modificar el inicio de contrato. Eso recalcula el prorrateo de vacaciones.
              Si solo cambias la jornada, cancela y usa la ficha del empleado → Cambio de contrato.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                guardar(true);
              }}
            >
              Sí, cambiar antigüedad
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ImportarEmpleadosDialog open={importOpen} onOpenChange={setImportOpen} onSuccess={cargar} />
    </div>
  );
}
